import moment from 'moment';
import { AnyObject } from '@xbcb/shared-types';
import { WorkOrderMilestoneName } from '@xbcb/work-order-types';
import { InvoiceStatus, ChargeCode } from '@xbcb/finance-types';
import type { UsIorContinuousBondRequest } from '@xbcb/api-gateway-client';

const getMilestoneNames = (record: AnyObject) => {
  return (
    record?.milestones.map(
      ({ name }: { name: WorkOrderMilestoneName }) => name,
    ) || []
  );
};

// checks if continuous bond was acquired successfully or not
// conditions to check:
// 1) a continuous bond acquisition request using bond bot failed and no subsequent success (ideally on a retry)
// 2) The charge bond query didn't succeed
export const continuousBondFailedFromMilestones = (
  record: AnyObject,
): boolean => {
  const milestoneNames = getMilestoneNames(record);
  return (
    milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_FAILED,
    ) &&
    !milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_SUCCEEDED,
    ) &&
    !milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_CHARGE_BOND_SUCCEEDED,
    )
  );
};

// checks if continuous bond was requested
export const continuousBondRequestedFromMilestones = (
  record: UsIorContinuousBondRequest,
): boolean => {
  const milestoneNames = getMilestoneNames(record);
  return milestoneNames.includes(
    WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_REQUESTED,
  );
};

export const continuousBondInvoiceExists = (
  record: UsIorContinuousBondRequest,
): boolean => {
  const invoiceExists = record?.group?.invoices?.some(
    ({ lines, status }) =>
      lines &&
      lines.some((line) => line.type === ChargeCode.CONTINUOUS_BOND) &&
      ![InvoiceStatus.PENDING_VOID, InvoiceStatus.VOID].includes(
        status as InvoiceStatus,
      ),
  );
  return Boolean(invoiceExists);
};

interface RecordWithContinuousBonds {
  usIor?: {
    continuousBonds?: {
      present?: {
        terminationDate?: string | null;
      } | null;
    } | null;
  } | null;
}

/**
 * Returns true if the IOR already has an active continuous bond.
 *
 * A `present` continuous bond is active when it has no `terminationDate`, or a
 * `terminationDate` that has not yet passed (today or later). Continuous bonds
 * auto-renew, so an active bond commonly carries a *future* termination date; it
 * is only inactive once that date is in the past. Mirrors the backend's canonical
 * active-bond definition in XbcbPartyWorkflow `importerQuery`
 * (`now.isSameOrBefore(terminationDate, 'day')`).
 *
 * Used to prevent redundant acquisition requests for offline-acquired bonds.
 * @param {RecordWithContinuousBonds} record - The bond request record containing usIor with continuousBonds data.
 * @return {boolean} Whether the IOR has an active continuous bond.
 */
export const hasActiveContinuousBond = (
  record: RecordWithContinuousBonds | null | undefined,
): boolean => {
  const presentBond = record?.usIor?.continuousBonds?.present;
  if (!presentBond) return false;
  const { terminationDate } = presentBond;
  return (
    !terminationDate || moment().isSameOrBefore(moment(terminationDate), 'day')
  );
};
